import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Switch,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import NewReleasesOutlinedIcon from "@mui/icons-material/NewReleasesOutlined";
import { menuItemsDef, settingsItemsDef } from "../../navigation/navDefinitions";
import { topbarHelpItems } from "../../navigation/topbarHelpDefinitions";
import { resolveGuideContent } from "./guideContent";
import { PLATFORM_APP_VERSION } from "../../appVersion";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useWorkspaceSegment } from "../../hooks/useWorkspaceSegment";
import { useEffectivePermissions, can } from "../../hooks/useEffectivePermissions";
import { useAuthContext } from "../../context/AuthContext";
import { resolveNavItemLabel } from "../../utils/workspaceSegmentLabels";
import { formatDatePtBR } from "../../utils/dateTime";
import {
  dashboardTabOrderForSegment,
  featureItemMatchesSegment,
  isPassengerTransportSegment,
  navDefMatchesSegment,
} from "./helpSegment";
import {
  buildHelpUserAccess,
  faqCategoriesForUser,
  featureItemMatchesPermissions,
  filterTopbarHelpItems,
  navDefMatchesPermissions,
} from "./helpAccess";
import {
  buildHelpUrl,
  helpAnchorId,
  parseHelpDeepLink,
  HELP_TAB_KEYS,
} from "./helpCenterIndex";
import { filterHelpItemsForUser, humanizeHelpFeature, humanizeSemicolonList, isInternalHelpItem } from "./helpUserCopy";
import { buildInventoryGroups, shouldFlattenInventoryGroups } from "./helpInventoryGroups";
import { filterUserFacingReleaseVersions } from "../../utils/releaseNotesUserFacing";

const MAIN_MATCH_ROUTES_BY_RES = {
  chat: ["/", "/chat", "/popup-call"],
  surveys: ["/surveys"],
};

const SETTINGS_MATCH_ROUTES_BY_RES = {
  users: ["/settings/users", "/user-details", "/users/"],
  chatbot: ["/settings/chatbot", "/users/:id/bot"],
  payment_methods: ["/settings/payment-methods", "/ecom/payment-methods"],
};

function buildMenuOrder(defs, matchByRes) {
  return defs.map((d) => ({
    key: d.res,
    textKey: d.textKey,
    label: d.label,
    res: d.res,
    link: d.link,
    segments: d.segments,
    matchRoutes: matchByRes?.[d.res] || [d.link],
  }));
}

const MENU_MAIN_ORDER = buildMenuOrder(menuItemsDef, MAIN_MATCH_ROUTES_BY_RES);
const MENU_SETTINGS_ORDER = buildMenuOrder(settingsItemsDef, SETTINGS_MATCH_ROUTES_BY_RES);

function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;

  const pushField = () => {
    row.push(field);
    field = "";
  };
  const pushRow = () => {
    if (row.length === 1 && row[0] === "") return;
    rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    const next = text[i + 1];
    if (inQuotes) {
      if (c === `"` && next === `"`) {
        field += `"`;
        i += 1;
      } else if (c === `"`) {
        inQuotes = false;
      } else {
        field += c;
      }
    } else if (c === `"`) {
      inQuotes = true;
    } else if (c === ",") {
      pushField();
    } else if (c === "\n") {
      pushField();
      pushRow();
    } else if (c === "\r") {
      // ignore
    } else {
      field += c;
    }
  }

  pushField();
  pushRow();

  const [header, ...body] = rows;
  if (!header || header.length < 3) return [];

  const norm = (s) => String(s || "").trim();
  const headerIdx = {};
  header.forEach((h, idx) => {
    headerIdx[norm(h)] = idx;
  });

  const get = (cols, r) => {
    for (const col of cols) {
      const j = headerIdx[col];
      if (j != null) return norm(r[j]);
    }
    return "";
  };

  return body
    .map((r) => ({
      surface: get(["surface"], r),
      area: get(["area"], r),
      page: get(["page"], r),
      route: get(["route"], r),
      feature: get(["feature"], r),
      variants: get(["variants"], r),
      permissions: get(["permissions"], r),
      segment: get(["segment"], r),
      notes: get(["notes"], r),
    }))
    .filter((x) => x.surface || x.area || x.page || x.route || x.feature);
}

function normalizeForSearch(v) {
  return String(v || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function groupBy(items, keyFn) {
  const map = new Map();
  items.forEach((it) => {
    const k = keyFn(it) || "—";
    const list = map.get(k) || [];
    list.push(it);
    map.set(k, list);
  });
  return map;
}

function dashboardTabAndTitle(feature) {
  const f = String(feature || "").trim();
  if (!f.toLowerCase().startsWith("aba:")) return null;
  // format: "Aba: <tab> — <title>"
  const rest = f.slice(4).trim();
  const parts = rest.split("—").map((x) => x.trim());
  const tab = parts[0] || "—";
  const title = parts.slice(1).join(" — ").trim() || "—";
  return { tab, title };
}

function normalizeDashboardTabName(tab) {
  const t = String(tab || "").trim();
  if (!t) return "—";
  // "Visão geral (Atendimento)" -> "Visão geral"
  const noParen = t.replace(/\s*\([^)]*\)\s*$/g, "").trim();
  // keep legacy CSV naming but normalize for grouping
  if (noParen.toLowerCase() === "passagens") return "Passagens rodoviárias";
  if (noParen.toLowerCase() === "passagens (segmento)") return "Passagens rodoviárias";
  return noParen;
}

function splitSemicolonList(v) {
  return String(v || "")
    .split(";")
    .map((x) => x.trim())
    .filter(Boolean);
}

function stripPrefixCI(value, prefix) {
  const v = String(value || "");
  const p = String(prefix || "");
  if (!v || !p) return v.trim();
  const escaped = p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`^${escaped}\\s*`, "i");
  return v.replace(re, "").trim();
}

function labelSurface(s) {
  const k = String(s || "").trim();
  const map = {
    app: "Dentro do sistema (equipe logada)",
    public_ecom: "Loja online (seus clientes)",
    public_websites: "Site público (seus clientes)",
    public_surveys: "Pesquisa por link (seus clientes)",
    platform_console: "Área administrativa Xpro",
    voice_sms: "Telefone e SMS",
  };
  return map[k] || (k ? k : "—");
}



function routeLooksLikeAppPath(route) {
  const r = String(route || "").trim();
  return r.startsWith("/");
}

function matchByPrefixes(route, prefixes) {
  const r = String(route || "").trim();
  if (!r) return false;
  return prefixes.some((p) => {
    const pref = String(p || "").trim();
    if (!pref) return false;
    // special placeholders we use above
    // IMPORTANT: "/" must match only the root route, otherwise it will swallow every app path.
    if (pref === "/") return r === "/";
    if (pref === "/users/:id/bot") return r.includes("/users/") && r.includes("/bot");
    if (pref === "/users/") return r.includes("/users/");
    if (pref === "/webhooks/") return r.includes("/webhooks/");
    if (pref === "/channels/") return r.includes("/channels/");
    if (pref === "/telephony/") return r.includes("/telephony/");
    if (pref === "/public/surveys") return r.includes("/public/surveys");
    if (pref === "/public/websites") return r.includes("/public/websites");
    if (pref === "/ecom/payment-methods") return r.includes("/ecom/payment-methods");
    return r === pref || r.startsWith(`${pref}/`) || r.startsWith(pref);
  });
}

function bucketForItem(it) {
  // Priorização: Settings primeiro, depois Main; se não bater, cai em “Outros”
  const route = it.route || "";

  for (const def of MENU_SETTINGS_ORDER) {
    if (matchByPrefixes(route, def.matchRoutes)) return { section: "settings", key: def.key };
  }
  for (const def of MENU_MAIN_ORDER) {
    if (matchByPrefixes(route, def.matchRoutes)) return { section: "main", key: def.key };
  }

  // Se não é rota de app (api_only, config, etc.), tenta inferir pela área/página
  const area = String(it.area || "").toLowerCase();
  const page = String(it.page || "").toLowerCase();
  if (area === "settings" && page.includes("channels")) return { section: "settings", key: "channels" };
  if (area === "settings" && page.includes("billing")) return { section: "settings", key: "billing" };
  if (String(it.surface || "") === "voice_sms") return { section: "settings", key: "channels" };

  return { section: "other", key: "other" };
}

function splitList(s) {
  return String(s || "")
    .split(/[;,]/g)
    .map((x) => x.trim())
    .filter(Boolean);
}

function normalizeSeg(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

const RELEASE_TYPE_LABELS = {
  feature: "Novidade",
  improvement: "Melhoria",
  fix: "Correção",
  other: "Outros",
};

const RELEASE_TYPE_COLORS = {
  feature: "success",
  improvement: "info",
  fix: "warning",
  other: "default",
};

export default function HelpPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation("app");
  const { segment, isLoading: isLoadingSegment } = useWorkspaceSegment();
  const { userData } = useAuthContext();
  const { data: permissionsData, isLoading: isLoadingPermissions } = useEffectivePermissions();
  const helpAccess = useMemo(
    () => buildHelpUserAccess({ userData, permissionsData }),
    [userData, permissionsData]
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLink = useMemo(() => parseHelpDeepLink(searchParams), [searchParams]);
  const segmentLabel = t(`platformPage.segmentOption.${segment}`, { defaultValue: segment });

  const [raw, setRaw] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState(deepLink.q || "");
  const [showExplanations, setShowExplanations] = useState(Boolean(deepLink.area || deepLink.faqCat));
  const [activeTab, setActiveTab] = useState(deepLink.tab ?? 0);
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [releaseNotesLoading, setReleaseNotesLoading] = useState(true);
  const [releaseNotesError, setReleaseNotesError] = useState("");

  const syncHelpUrl = React.useCallback(
    (patch = {}) => {
      const merged = {
        tab: patch.tab ?? activeTab,
        section: patch.section ?? deepLink.section,
        area: patch.area ?? deepLink.area,
        faqCat: patch.faqCat ?? deepLink.faqCat,
        faqItem: patch.faqItem ?? deepLink.faqItem,
        q: patch.q ?? query,
      };
      const qs = buildHelpUrl({
        tab: merged.tab,
        section: merged.section,
        area: merged.area,
        faqCat: merged.faqCat,
        faqItem: merged.faqItem,
        q: merged.q,
      });
      setSearchParams(qs.includes("?") ? qs.split("?")[1] : "", { replace: true });
    },
    [activeTab, deepLink, query, setSearchParams]
  );

  useEffect(() => {
    let cancelled = false;
    setReleaseNotesLoading(true);
    setReleaseNotesError("");
    fetch("/release-notes.json", { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (!cancelled) {
          setReleaseNotes(
            filterUserFacingReleaseVersions(Array.isArray(data?.versions) ? data.versions : [])
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReleaseNotesError("Não foi possível carregar o histórico de versões agora.");
        }
      })
      .finally(() => {
        if (!cancelled) setReleaseNotesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetch("/feature-map.csv", { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((t) => {
        if (!cancelled) setRaw(t || "");
      })
      .catch(() => {
        if (!cancelled) {
          setError("Não foi possível carregar o guia agora. Atualize a página e tente de novo.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setActiveTab(deepLink.tab ?? 0);
    if (deepLink.q) setQuery(deepLink.q);
    if (deepLink.area || deepLink.faqCat) setShowExplanations(true);
  }, [deepLink.tab, deepLink.q, deepLink.area, deepLink.faqCat, deepLink.faqItem, deepLink.section]);

  const allItems = useMemo(() => parseCsv(raw), [raw]);
  const userFacingItems = useMemo(
    () => filterHelpItemsForUser(allItems, helpAccess),
    [allItems, helpAccess]
  );

  const filteredByQuery = useMemo(() => {
    const q = normalizeForSearch(query);
    if (!q) return userFacingItems;
    return userFacingItems.filter((it) => {
      const hay = [
        it.surface,
        it.area,
        it.page,
        it.route,
        it.feature,
        it.displayFeature,
        it.variants,
        it.displayDescription,
        it.permissions,
        it.displayPermission,
        it.segment,
        it.notes,
      ]
        .map(normalizeForSearch)
        .join(" | ");
      return hay.includes(q);
    });
  }, [userFacingItems, query]);

  const filteredFaq = useMemo(() => {
    const base = faqCategoriesForUser(segment, helpAccess);
    const q = normalizeForSearch(query);
    if (!q) return base;
    return base
      .map((cat) => ({
        ...cat,
        items: cat.items.filter((item) =>
          normalizeForSearch(`${cat.title} ${item.q} ${item.a}`).includes(q)
        ),
      }))
      .filter((cat) => cat.items.length > 0);
  }, [query, segment]);

  const filteredReleaseNotes = useMemo(() => {
    const q = normalizeForSearch(query);
    if (!q) return releaseNotes;
    return releaseNotes
      .map((v) => ({
        ...v,
        changes: (v.changes || []).filter((c) =>
          normalizeForSearch(
            `${v.title || ""} ${v.version} ${formatDatePtBR(v.date)} ${v.date || ""} ${c.message} ${c.type}`
          ).includes(q)
        ),
      }))
      .filter((v) => (v.changes || []).length > 0);
  }, [releaseNotes, query]);

  const filteredBySegment = useMemo(
    () =>
      filteredByQuery.filter(
        (it) =>
          featureItemMatchesSegment(it, segment) && featureItemMatchesPermissions(it, helpAccess)
      ),
    [filteredByQuery, segment, helpAccess]
  );

  const filtered = filteredBySegment;

  const grouped = useMemo(() => {
    const withBucket = filtered.map((it) => ({ ...it, __bucket: bucketForItem(it) }));
    const main = withBucket.filter((x) => x.__bucket.section === "main");
    const settings = withBucket.filter((x) => x.__bucket.section === "settings");
    const other = withBucket.filter((x) => x.__bucket.section === "other");

    const buildItemGroup = (items, menuKey, menuLabel) =>
      buildInventoryGroups(items, { menuKey, menuLabel });

    const buildOrderedGroup = (defs, items) => {
      return defs
        .filter((def) => navDefMatchesSegment(def, segment) && navDefMatchesPermissions(def, helpAccess))
        .map((def) => {
          const inBucket = items.filter((x) => x.__bucket.key === def.key);
          return {
            key: def.key,
            label: def.label || resolveNavItemLabel(def, segment, t) || t(def.textKey),
            count: inBucket.length,
            items: inBucket,
            pages: buildItemGroup(inBucket, def.key, def.label || resolveNavItemLabel(def, segment, t) || t(def.textKey)),
          };
        })
        .filter((g) => {
          if (g.count > 0) return true;
          const section = MENU_MAIN_ORDER.some((d) => d.key === g.key) ? "main" : "settings";
          return Boolean(resolveGuideContent(section, g.key, segment));
        });
    };

    const mainGroups = buildOrderedGroup(MENU_MAIN_ORDER, main);
    const settingsGroups = buildOrderedGroup(MENU_SETTINGS_ORDER, settings);

    // “Outros”: mantém agrupamento por surface/area como fallback
    const bySurface = groupBy(other, (x) => x.surface);
    const surfaces = Array.from(bySurface.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    const otherGroups = surfaces
      .map(([surfaceKey, surfaceItems]) => {
        const byArea = groupBy(surfaceItems, (x) => x.area);
        const areas = Array.from(byArea.entries()).sort((a, b) => a[0].localeCompare(b[0]));
        return {
          surfaceKey,
          surfaceLabel: labelSurface(surfaceKey),
          count: surfaceItems.length,
          areas: areas
            .map(([areaKey, areaItems]) => ({
              areaKey,
              count: areaItems.length,
              pages: buildInventoryGroups(areaItems),
            }))
            .filter((a) => a.count > 0),
        };
      })
      .filter((g) => g.count > 0);

    return { mainGroups, settingsGroups, otherGroups };
  }, [filtered, segment, t, helpAccess]);

  useEffect(() => {
    if (loading || isLoadingSegment || isLoadingPermissions) return undefined;
    const anchor = helpAnchorId({
      section: deepLink.section,
      area: deepLink.area,
      faqCat: deepLink.faqCat,
      faqItem: deepLink.faqItem,
    });
    const timer = window.setTimeout(() => {
      document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [loading, isLoadingSegment, isLoadingPermissions, deepLink, grouped.mainGroups.length, grouped.settingsGroups.length]);

  const totalFeatures = filtered.length;
  const totalAll = useMemo(
    () =>
      allItems.filter(
        (it) =>
          featureItemMatchesSegment(it, segment) &&
          featureItemMatchesPermissions(it, helpAccess) &&
          !isInternalHelpItem(it, helpAccess)
      ).length,
    [allItems, segment, helpAccess]
  );

  const topbarModel = useMemo(
    () => filterTopbarHelpItems(topbarHelpItems, helpAccess),
    [helpAccess]
  );

  const quickStart = useMemo(() => {
    const isPassenger = isPassengerTransportSegment(segment);
    const items = [
      { key: "chat", label: "Atendimento", route: "/chat" },
      { key: "clients", label: "Clientes", route: "/clients" },
      { key: "channels", label: "Canais", route: "/settings/channels" },
      { key: "users", label: "Usuários", route: "/settings/users" },
      {
        key: "products",
        label: isPassenger ? t("nav.trechos") : t("nav.products"),
        route: "/products",
      },
      { key: "billing", label: "Plano e faturamento", route: "/settings/billing" },
    ];
    if (isPassenger) {
      items.splice(5, 0, { key: "cities", label: t("nav.passengerCities"), route: "/passenger-cities" });
    }
    return items.filter((item) => can(helpAccess.scopeSet, item.key, "view"));
  }, [segment, t, helpAccess.scopeSet]);

  const renderFeatureList = (items, keyPrefix) => (
    <List dense disablePadding>
      {(items || []).map((it, idx) => (
        <ListItem key={`${keyPrefix}-${it.feature}-${idx}`} disableGutters sx={{ py: 0.5 }}>
          <ListItemText
            primary={
              <Typography sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                {it.displayFeature || it.feature || "—"}
              </Typography>
            }
            secondary={
              <>
                {it.displayDescription && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.5 }}>
                    {it.displayDescription}
                  </Typography>
                )}
                {it.displayPermission && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                    <Chip size="small" label={it.displayPermission} />
                  </Stack>
                )}
              </>
            }
          />
        </ListItem>
      ))}
    </List>
  );

  const renderPage = (p, keyPrefix) => {
    const label = p.displayLabel || p.page || "—";
    const openScreenBtn =
      p.route && p.route !== "—" && routeLooksLikeAppPath(p.route) ? (
        <Button component={RouterLink} to={p.route} size="small" variant="outlined">
          Ir para esta tela
        </Button>
      ) : null;

    return (
      <Accordion key={`${keyPrefix}-${p.groupKey || p.page}-${p.route}`} disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ width: "100%" }}>
            <Typography sx={{ fontWeight: 700 }}>{label}</Typography>
            <Chip size="small" label={`${p.count}`} variant="outlined" />
            <Box sx={{ flex: 1 }} />
            {openScreenBtn}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>{renderFeatureList(p.items, `${keyPrefix}-${label}`)}</AccordionDetails>
      </Accordion>
    );
  };

  const renderMenuInventory = (g, keyPrefix) => {
    if (g.key === "dashboard") return renderDashboardTree(g.items || []);
    if (g.pages.length === 0) {
      return (
        <Alert severity="info">
          Ainda não há descrições detalhadas para esta área. Use as dicas acima ou fale com o administrador.
        </Alert>
      );
    }
    if (shouldFlattenInventoryGroups(g.pages, g.label)) {
      const allItems = g.pages.flatMap((p) => p.items || []);
      return renderFeatureList(allItems, `${keyPrefix}-${g.key}-flat`);
    }
    return g.pages.map((p) => renderPage(p, `${keyPrefix}-${g.key}`));
  };

  const renderDashboardTree = (items) => {
    const dash = items.filter((it) => String(it.route || "").trim() === "/dashboard");
    const geral = dash.filter((it) => !String(it.feature || "").toLowerCase().startsWith("aba:"));
    const tabs = dash
      .map((it) => ({ it, parsed: dashboardTabAndTitle(it.feature) }))
      .filter((x) => x.parsed)
      .map((x) => ({
        ...x.parsed,
        tab: normalizeDashboardTabName(x.parsed.tab),
        it: x.it,
      }));

    const tabOrder = dashboardTabOrderForSegment(segment);
    const tabsByName = groupBy(tabs, (x) => x.tab);

    const renderFeatureRow = (it, keyPrefix) => {
      return (
        <ListItem key={`${keyPrefix}-${it.feature}`} disableGutters sx={{ py: 0.5 }}>
          <ListItemText
            primary={
              <Typography sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                {it.displayFeature || it.feature || "—"}
              </Typography>
            }
            secondary={
              it.displayDescription ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.5 }}>
                  {it.displayDescription}
                </Typography>
              ) : null
            }
          />
        </ListItem>
      );
    };

    const renderTabItem = (x, keyPrefix) => {
      const title = x.title;
      const isKpis = String(title).toLowerCase() === "kpis";
      if (isKpis) {
        const cards = humanizeSemicolonList(x.it.variants);
        return (
          <Box key={`${keyPrefix}-${x.tab}-kpis`} sx={{ mb: 1 }}>
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Indicadores em destaque</Typography>
            {cards.length === 0 ? (
              <Alert severity="info">Os indicadores desta aba ainda não foram descritos aqui.</Alert>
            ) : (
              <List dense disablePadding>
                {cards.map((c) => (
                  <ListItem key={`${keyPrefix}-${x.tab}-kpi-${c}`} disableGutters sx={{ py: 0.4 }}>
                    <ListItemText
                      primary={<Typography sx={{ fontWeight: 600 }}>{c}</Typography>}
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.15, lineHeight: 1.5 }}>
                          {x.it.displayDescription ||
                            "Mostra um resumo do período selecionado para apoiar decisões rápidas."}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        );
      }

      return (
        <ListItem key={`${keyPrefix}-${x.tab}-${title}`} disableGutters sx={{ py: 0.5 }}>
          <ListItemText
            primary={<Typography sx={{ fontWeight: 600, lineHeight: 1.35 }}>{title || "—"}</Typography>}
            secondary={
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.5 }}>
                {x.it.displayDescription || "—"}
              </Typography>
            }
          />
        </ListItem>
      );
    };

    return (
      <Stack spacing={1}>
        <Accordion defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontWeight: 800 }}>Geral</Typography>
              <Chip size="small" label={`${geral.length}`} variant="outlined" />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {showExplanations && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.6 }}>
                Opções que valem para todo o painel: período das datas, exportação e recursos gerais.
              </Typography>
            )}
            {geral.length === 0 ? (
              <Alert severity="info">Ainda não há explicações gerais cadastradas para o Dashboard.</Alert>
            ) : (
              <List dense disablePadding>
                {geral.map((it) => renderFeatureRow(it, "dash-geral"))}
              </List>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontWeight: 800 }}>Visões por assunto</Typography>
              <Chip size="small" label={`${tabs.length}`} variant="outlined" />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {tabOrder.map((tabName) => {
                const list = tabsByName.get(tabName) || [];
                return (
                  <Accordion key={`dash-tab-${tabName}`} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontWeight: 700 }}>{tabName}</Typography>
                        <Chip size="small" label={`${list.length}`} variant="outlined" />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      {showExplanations && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.6 }}>
                          Números e gráficos desta aba, considerando o período escolhido no topo.
                        </Typography>
                      )}
                      {list.length === 0 ? (
                        <Alert severity="info">Ainda não há itens descritos para esta aba.</Alert>
                      ) : (
                        <List dense disablePadding>
                          {list.map((x) => renderTabItem(x, `dash-${tabName}`))}
                        </List>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
    );
  };

  const renderL2PrefixedTree = ({
    items,
    route,
    prefix,
    generalTitle = "Geral",
    generalEmptyText = "Sem itens gerais mapeados ainda.",
    generalHelpText,
    childrenTitle,
    emptyHintPrefixLabel,
    order = [],
  }) => {
    const all = items.filter((it) => String(it.route || "").trim() === route);
    const prefLc = String(prefix || "").toLowerCase();
    const children = all
      .filter((it) => String(it.feature || "").toLowerCase().startsWith(prefLc))
      .map((it) => ({ name: stripPrefixCI(it.feature, prefix) || "—", it }));
    const geral = all.filter((it) => !String(it.feature || "").toLowerCase().startsWith(prefLc));

    const byName = new Map();
    children.forEach((c) => byName.set(c.name, c.it));
    const ordered = [
      ...order.filter((n) => byName.has(n)).map((n) => ({ name: n, it: byName.get(n) })),
      ...children
        .filter((c) => !order.includes(c.name))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((c) => ({ name: c.name, it: c.it })),
    ];

    const renderGeneralRow = (it, keyPrefix) => (
      <ListItem key={`${keyPrefix}-${it.feature}`} disableGutters sx={{ py: 0.5 }}>
        <ListItemText
          primary={<Typography sx={{ fontWeight: 600, lineHeight: 1.35 }}>{it.displayFeature || it.feature || "—"}</Typography>}
          secondary={
            <>
              {it.displayDescription && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.5 }}>
                  {it.displayDescription}
                </Typography>
              )}
              {it.displayPermission && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                  <Chip size="small" label={it.displayPermission} />
                </Stack>
              )}
            </>
          }
        />
      </ListItem>
    );

    return (
      <Stack spacing={1}>
        <Accordion defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontWeight: 800 }}>{generalTitle}</Typography>
              <Chip size="small" label={`${geral.length}`} variant="outlined" />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {showExplanations && generalHelpText && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.6 }}>
                {generalHelpText}
              </Typography>
            )}
            {geral.length === 0 ? (
              <Alert severity="info">{generalEmptyText}</Alert>
            ) : (
              <List dense disablePadding>
                {geral.map((it) => renderGeneralRow(it, `l2-${route}-geral`))}
              </List>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography sx={{ fontWeight: 800 }}>{childrenTitle}</Typography>
              <Chip size="small" label={`${ordered.length}`} variant="outlined" />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {ordered.map(({ name, it }) => (
                <Accordion key={`l2-${route}-${prefix}-${name}`} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ width: "100%" }}>
                      <Typography sx={{ fontWeight: 700 }}>{humanizeHelpFeature(name)}</Typography>
                      <Box sx={{ flex: 1 }} />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {it.displayDescription || "—"}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
              {ordered.length === 0 && (
                <Alert severity="info">
                  Ainda não há opções descritas nesta seção.
                </Alert>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
    );
  };

  const renderPaymentMethodsTree = (items) =>
    renderL2PrefixedTree({
      items,
      route: "/settings/payment-methods",
      prefix: "Provedor:",
      childrenTitle: "Formas disponíveis",
      emptyHintPrefixLabel: "Provedor:",
      order: [
        "Default",
        "PIX Buspay",
        "Pagar.me",
        "Cielo",
        "Pague Fácil",
        "Dinheiro",
        "Mercado Pago",
        "Xpay",
        "PIX Itaú",
        "Rede",
        "Adyen",
        "AppMax — link / checkout",
        "AppMax — PIX (API)",
        "AppMax — cartão (API)",
      ],
      generalHelpText: "Cadastro geral, ativação e regras que valem para qualquer forma de pagamento.",
      generalEmptyText: "Ainda não há explicações gerais sobre formas de pagamento.",
    });

  const renderDeliveryMethodsTree = (items) =>
    renderL2PrefixedTree({
      items,
      route: "/settings/delivery-methods",
      prefix: "Método:",
      childrenTitle: "Opções de entrega",
      emptyHintPrefixLabel: "Método:",
      order: ["Correios", "Vuxx", "Retirada", "Gestão de Envios", "Fallback por fator/peso"],
      generalHelpText: "Cadastro, ativação e regras gerais de entrega (região, prazo, valor mínimo etc.).",
      generalEmptyText: "Ainda não há explicações gerais sobre métodos de entrega.",
    });

  const renderChannelsTree = (items) =>
    renderL2PrefixedTree({
      items,
      route: "/settings/channels",
      prefix: "Provedor:",
      childrenTitle: "Canais disponíveis",
      emptyHintPrefixLabel: "Provedor:",
      order: [
        "WhatsApp — Meta",
        "WhatsApp — Gupshup",
        "WhatsApp — API Fácil",
        "Meta — Instagram/Messenger",
        "Email — AWS SES",
        "Website (chat no site)",
        "Telefonia Xpro",
        "SMS",
        "SMS — Clickatell",
      ],
      generalHelpText: "Como adicionar canais, conectar, verificar se estão funcionando e definir padrões de atendimento.",
      generalEmptyText: "Ainda não há explicações gerais sobre canais.",
    });

  const renderIntegrationsTree = (items) =>
    renderL2PrefixedTree({
      items,
      route: "/settings/integrations",
      prefix: "Provedor:",
      childrenTitle: "Serviços conectados",
      emptyHintPrefixLabel: "Provedor:",
      order: ["Passagens rodoviárias (TotalBus)", "Telefonia automática (passagens)"],
      generalHelpText: "Como cadastrar serviços externos, testar se a conexão funciona e acompanhar problemas.",
      generalEmptyText: "Ainda não há explicações gerais sobre integrações.",
    });

  const renderAntifraudTree = (items) =>
    renderL2PrefixedTree({
      items,
      route: "/settings/antifraud-methods",
      prefix: "Provedor:",
      childrenTitle: "Serviços de antifraude",
      emptyHintPrefixLabel: "Provedor:",
      order: ["Konduto", "ClearSale", "FControl"],
      generalHelpText: "Como cadastrar serviços antifraude e alinhar o que fazer em cada resultado de análise.",
      generalEmptyText: "Ainda não há explicações gerais sobre antifraude.",
    });

  return (
    <Box sx={{ pt: 1, pb: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Central de Ajuda
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.6 }}>
          Tudo explicado em linguagem simples, <strong>adaptado ao tipo de operação da sua empresa</strong> (
          {segmentLabel}). Inclui o que cada área faz, respostas frequentes e novidades. Versão em uso:{" "}
          <strong>{PLATFORM_APP_VERSION}</strong>.
        </Typography>
        {!isLoadingSegment && (
          <Chip size="small" label={segmentLabel} color="primary" variant="outlined" sx={{ mt: 1 }} />
        )}
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, v) => {
          setActiveTab(v);
          syncHelpUrl({ tab: v, area: v === HELP_TAB_KEYS.guide ? deepLink.area : "", faqCat: v === HELP_TAB_KEYS.faq ? deepLink.faqCat : "" });
        }}
        variant={isMobile ? "fullWidth" : "standard"}
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Como usar cada área" />
        <Tab label="Perguntas frequentes" />
        <Tab label="O que há de novo" icon={<NewReleasesOutlinedIcon />} iconPosition="start" />
      </Tabs>

      <Stack
        direction={isMobile ? "column" : "row"}
        spacing={1.5}
        sx={{ mb: 2, alignItems: isMobile ? "stretch" : "center" }}
      >
        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={
            activeTab === 0
              ? 'Buscar: "whatsapp", "clientes", "pagamento", "dashboard"...'
              : activeTab === 1
                ? 'Buscar: "conversa", "canais", "senha", "equipe"...'
                : 'Buscar: "melhoria", "correção", "atendimento"...'
          }
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {activeTab === 0 && (
        <>
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={showExplanations}
              onChange={(e) => setShowExplanations(e.target.checked)}
              color="primary"
            />
          }
          label="Mostrar dicas de uso em cada área"
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={`${totalFeatures} tópicos encontrados`}
            color="primary"
            variant="outlined"
          />
          <Chip
            size="small"
            label={`${totalAll} tópicos no guia`}
            variant="outlined"
          />
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
            Mostramos só o que combina com seu perfil de acesso e o tipo de operação da empresa.
          </Typography>
        </Stack>
      </Box>

      {quickStart.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 800, mb: 1 }}>Comece por aqui</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {quickStart.map((c) => (
              <Button
                key={c.key}
                component={RouterLink}
                to={c.route}
                variant="contained"
                size="small"
              >
                {c.label}
              </Button>
            ))}
          </Stack>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}{" "}
          <Link component="button" onClick={() => window.location.reload()}>
            Atualizar
          </Link>
        </Alert>
      )}

      {loading && (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Carregando o guia…
          </Typography>
        </Box>
      )}

      {!loading && isLoadingPermissions && (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Ajustando conteúdo ao seu perfil…
          </Typography>
        </Box>
      )}

      {!loading && !isLoadingPermissions && !error && filtered.length === 0 && (
        <Alert severity="info">
          Não encontramos nada com esse termo. Tente palavras mais simples, como &quot;chat&quot;, &quot;cliente&quot; ou &quot;pagamento&quot;.
        </Alert>
      )}

      {!loading && !isLoadingPermissions && (grouped.mainGroups.length > 0 || grouped.settingsGroups.length > 0 || grouped.otherGroups.length > 0 || helpAccess.isPlatformAdmin || helpAccess.isPartner) && (
        <Box>
          {/* Menu principal */}
          <Accordion defaultExpanded id="help-nav-main">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontWeight: 800 }}>Seu trabalho do dia a dia</Typography>
                <Chip size="small" label={`${grouped.mainGroups.reduce((a, g) => a + g.count, 0)}`} variant="outlined" />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                {grouped.mainGroups.map((g) => (
                  <Accordion
                    key={`main-${g.key}`}
                    id={helpAnchorId({ section: "main", area: g.key })}
                    defaultExpanded={!deepLink.area || deepLink.area === g.key}
                    disableGutters
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontWeight: 700 }}>{g.label}</Typography>
                        <Chip size="small" label={`${g.count}`} variant="outlined" />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      {showExplanations && resolveGuideContent("main", g.key, segment) && (() => {
                        const guide = resolveGuideContent("main", g.key, segment);
                        return (
                        <Box sx={{ mb: 1.25 }}>
                          <Typography sx={{ fontWeight: 700 }}>
                            {guide.title}
                          </Typography>
                          {guide.what && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.55 }}>
                              {guide.what}
                            </Typography>
                          )}
                          {Array.isArray(guide.steps) && guide.steps.length > 0 && (
                            <Box sx={{ mt: 0.75 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                Como usar
                              </Typography>
                              <Box component="ol" sx={{ m: 0.5, pl: 2.5 }}>
                                {guide.steps.map((s) => (
                                  <li key={s}>
                                    <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                                      {s}
                                    </Typography>
                                  </li>
                                ))}
                              </Box>
                            </Box>
                          )}
                          {Array.isArray(guide.tips) && guide.tips.length > 0 && (
                            <Box sx={{ mt: 0.75 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                Dica
                              </Typography>
                              {guide.tips.map((tip) => (
                                <Typography key={tip} variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.55 }}>
                                  {tip}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                        );
                      })()}
                      <Stack spacing={0.75}>{renderMenuInventory(g, "main")}</Stack>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Configurações */}
          <Accordion
            defaultExpanded={deepLink.section === "settings" || !!deepLink.area}
            id="help-nav-settings"
            sx={{ mt: 1.5 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontWeight: 800 }}>Ajustes da empresa</Typography>
                <Chip size="small" label={`${grouped.settingsGroups.reduce((a, g) => a + g.count, 0)}`} variant="outlined" />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                {grouped.settingsGroups.map((g) => (
                  <Accordion
                    key={`settings-${g.key}`}
                    id={helpAnchorId({ section: "settings", area: g.key })}
                    defaultExpanded={!deepLink.area || deepLink.area === g.key}
                    disableGutters
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontWeight: 700 }}>{g.label}</Typography>
                        <Chip size="small" label={`${g.count}`} variant="outlined" />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      {showExplanations && resolveGuideContent("settings", g.key, segment) && (() => {
                        const guide = resolveGuideContent("settings", g.key, segment);
                        return (
                        <Box sx={{ mb: 1.25 }}>
                          <Typography sx={{ fontWeight: 700 }}>
                            {guide.title}
                          </Typography>
                          {guide.what && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.55 }}>
                              {guide.what}
                            </Typography>
                          )}
                          {Array.isArray(guide.steps) && guide.steps.length > 0 && (
                            <Box sx={{ mt: 0.75 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                Como usar
                              </Typography>
                              <Box component="ol" sx={{ m: 0.5, pl: 2.5 }}>
                                {guide.steps.map((s) => (
                                  <li key={s}>
                                    <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                                      {s}
                                    </Typography>
                                  </li>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                        );
                      })()}
                      <Stack spacing={0.75}>
                        {g.key === "payment_methods" ? (
                          renderPaymentMethodsTree(g.items || [])
                        ) : g.key === "delivery_methods" ? (
                          renderDeliveryMethodsTree(g.items || [])
                        ) : g.key === "channels" ? (
                          renderChannelsTree(g.items || [])
                        ) : g.key === "integrations" ? (
                          renderIntegrationsTree(g.items || [])
                        ) : g.key === "antifraud_methods" ? (
                          renderAntifraudTree(g.items || [])
                        ) : (
                          renderMenuInventory(g, "settings")
                        )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {helpAccess.isPlatformAdmin && resolveGuideContent("platform", "overview", segment) && (
            <Accordion defaultExpanded={deepLink.section === "platform"} id="help-nav-platform" sx={{ mt: 1.5 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography sx={{ fontWeight: 800 }}>Console da plataforma</Typography>
                  <Chip size="small" label={`${grouped.otherGroups.find((g) => g.surfaceKey === "platform_console")?.count || 0}`} variant="outlined" />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {["overview", "ask_interno", "governance", "integrations"].map((key) => {
                  const guide = resolveGuideContent("platform", key, segment);
                  if (!guide) return null;
                  return (
                    <Box key={key} sx={{ mb: 1.5 }}>
                      <Typography sx={{ fontWeight: 700 }}>{guide.title}</Typography>
                      {guide.what && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.55 }}>
                          {guide.what}
                        </Typography>
                      )}
                      {Array.isArray(guide.steps) && guide.steps.length > 0 && (
                        <Box component="ol" sx={{ m: 0.75, pl: 2.5 }}>
                          {guide.steps.map((s) => (
                            <li key={s}>
                              <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                                {s}
                              </Typography>
                            </li>
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}
                <Stack spacing={0.75}>
                  {(grouped.otherGroups.find((g) => g.surfaceKey === "platform_console")?.areas || [])
                    .flatMap((area) => area.pages)
                    .map((p) => renderPage(p, "platform-console"))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          {helpAccess.isPartner && resolveGuideContent("partner", "overview", segment) && (
            <Accordion defaultExpanded={deepLink.section === "partner"} id="help-nav-partner" sx={{ mt: 1.5 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 800 }}>Portal do parceiro</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {(() => {
                  const guide = resolveGuideContent("partner", "overview", segment);
                  return (
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>{guide.title}</Typography>
                      {guide.what && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.55 }}>
                          {guide.what}
                        </Typography>
                      )}
                      {Array.isArray(guide.steps) && guide.steps.length > 0 && (
                        <Box component="ol" sx={{ m: 0.75, pl: 2.5 }}>
                          {guide.steps.map((s) => (
                            <li key={s}>
                              <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                                {s}
                              </Typography>
                            </li>
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              </AccordionDetails>
            </Accordion>
          )}

          {grouped.otherGroups
            .filter((g) => g.surfaceKey !== "platform_console")
            .map((surfaceGroup) => (
              <Accordion key={`help-other-${surfaceGroup.surfaceKey}`} sx={{ mt: 1.5 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography sx={{ fontWeight: 800 }}>{surfaceGroup.surfaceLabel}</Typography>
                    <Chip size="small" label={`${surfaceGroup.count}`} variant="outlined" />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={0.75}>
                    {surfaceGroup.areas.flatMap((area) =>
                      area.pages.map((p) => renderPage(p, `other-${surfaceGroup.surfaceKey}-${area.areaKey}`))
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}

          {/* Topo (Topbar) */}
          <Accordion defaultExpanded={deepLink.section === "topbar"} id="help-nav-topbar" sx={{ mt: 1.5 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontWeight: 800 }}>Parte de cima da tela</Typography>
                <Chip size="small" label={`${topbarModel.length}`} variant="outlined" />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1}>
                {topbarModel.map((it) => (
                  <Accordion key={`top-${it.key}`} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontWeight: 700 }}>{it.label}</Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      {resolveGuideContent("topbar", it.key, segment)?.what && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                            {resolveGuideContent("topbar", it.key, segment).what}
                          </Typography>
                        </Box>
                      )}

                      {it.kind === "group" && Array.isArray(it.children) && it.children.length > 0 ? (
                        <Stack spacing={0.75}>
                          {it.children.map((ch) => (
                            <Box
                              key={`top-${it.key}-${ch.key}`}
                              sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                p: 1.25,
                                bgcolor: "background.paper",
                              }}
                            >
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography sx={{ fontWeight: 600 }}>{ch.label}</Typography>
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                      ) : null}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
        </>
      )}

      {activeTab === 1 && (
        <Box>
          {filteredFaq.length === 0 ? (
            <Alert severity="info">
              Nenhuma pergunta encontrada. Tente termos como &quot;whatsapp&quot;, &quot;conversa&quot; ou &quot;perfil&quot;.
            </Alert>
          ) : (
            <Stack spacing={1.5}>
              {filteredFaq.map((cat) => (
                <Accordion
                  key={`faq-${cat.key}`}
                  id={helpAnchorId({ faqCat: cat.key })}
                  defaultExpanded={!deepLink.faqCat || deepLink.faqCat === cat.key}
                  disableGutters
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 800 }}>{cat.title}</Typography>
                    <Chip size="small" label={cat.items.length} variant="outlined" sx={{ ml: 1 }} />
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={1}>
                      {cat.items.map((item, idx) => (
                        <Accordion
                          key={`faq-${cat.key}-${idx}`}
                          id={helpAnchorId({ faqCat: cat.key, faqItem: idx })}
                          defaultExpanded={
                            deepLink.faqCat === cat.key &&
                            (deepLink.faqItem == null || deepLink.faqItem === idx)
                          }
                          disableGutters
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography sx={{ fontWeight: 600, lineHeight: 1.4 }}>{item.q}</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                              {item.a}
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          )}
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
            Veja o que melhorou ou foi corrigido a cada atualização do sistema. A versão que você usa hoje está
            destacada abaixo.
          </Typography>

          {releaseNotesError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {releaseNotesError}
            </Alert>
          )}

          {releaseNotesLoading && (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Carregando novidades…
              </Typography>
            </Box>
          )}

          {!releaseNotesLoading && filteredReleaseNotes.length === 0 && (
            <Alert severity="info">
              {query
                ? "Nenhuma novidade encontrada com esse termo."
                : "Ainda não há registros de versão publicados."}
            </Alert>
          )}

          {!releaseNotesLoading && filteredReleaseNotes.length > 0 && (
            <Stack spacing={1}>
              {filteredReleaseNotes.map((v) => (
                <Accordion
                  key={`rel-${v.period || v.version}`}
                  defaultExpanded={v.version === PLATFORM_APP_VERSION}
                  disableGutters
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography sx={{ fontWeight: 800 }}>
                        {v.title || `Versão ${v.version}`}
                      </Typography>
                      {v.version === PLATFORM_APP_VERSION && (
                        <Chip size="small" color="primary" label="Versão atual" />
                      )}
                      {v.version && (
                        <Chip size="small" variant="outlined" label={`v${v.version}`} />
                      )}
                      {v.date && (
                        <Chip size="small" variant="outlined" label={formatDatePtBR(v.date)} />
                      )}
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${(v.changes || []).length} novidade(s)`}
                      />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    {(v.changes || []).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Sem itens registrados nesta versão.
                      </Typography>
                    ) : (
                      <List dense disablePadding>
                        {(v.changes || []).map((c, idx) => (
                          <ListItem key={`rel-${v.version}-${idx}`} disableGutters sx={{ py: 0.5, alignItems: "flex-start" }}>
                            <Chip
                              size="small"
                              label={RELEASE_TYPE_LABELS[c.type] || RELEASE_TYPE_LABELS.other}
                              color={RELEASE_TYPE_COLORS[c.type] || "default"}
                              sx={{ mr: 1, mt: 0.25, flexShrink: 0 }}
                            />
                            <ListItemText
                              primary={
                                <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
                                  {c.message}
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
}

